/**
 * Next-Gen Modern Glassmorphic Login & SignUp UI View.
 * Provides sleek authentication flow with Google/Email login, persistent sessions,
 * and seamless redirect to the NumixSheet Dashboard.
 */

import { setActiveUser, type UserProfile } from '../core/authStore.js';
import { apiUrl } from '../core/apiClient.js';
import { el } from './dom.js';

export interface LoginViewOptions {
  onLoginSuccess: (user: UserProfile) => void;
}

export function createLoginView(options: LoginViewOptions): HTMLElement {
  const container = el('div', {
    className: 'rz-login-page-backdrop',
    attrs: {
      style: `
        position: fixed; inset: 0; z-index: 10000;
        background: radial-gradient(circle at 50% 20%, #0f172a 0%, #020617 100%);
        display: flex; align-items: center; justify-content: center;
        font-family: Inter, system-ui, -apple-system, sans-serif; overflow: auto; padding: 20px;
      `,
    },
  });

  const card = el('div', {
    className: 'rz-login-card',
    attrs: {
      style: `
        width: 100%; max-width: 440px; background: rgba(30, 41, 59, 0.7);
        backdrop-filter: blur(16px); border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 20px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 30px rgba(13, 148, 136, 0.2);
        padding: 40px 36px; text-align: center; color: #f8fafc; box-sizing: border-box;
      `,
    },
  });

  // Logo Badge
  const logoBox = el('div', {
    attrs: {
      style: `
        display: inline-flex; align-items: center; justify-content: center;
        width: 56px; height: 56px; border-radius: 14px;
        background: linear-gradient(135deg, #0d9488 0%, #0284c7 100%);
        box-shadow: 0 8px 20px rgba(13, 148, 136, 0.4); margin-bottom: 20px;
      `,
    },
  }, [
    el('span', { text: '⚡', attrs: { style: 'font-size: 28px;' } }),
  ]);

  const title = el('h2', {
    text: 'Welcome to NumixSheet',
    attrs: { style: 'font-size: 24px; font-weight: 700; margin: 0 0 8px 0; letter-spacing: -0.5px;' },
  });

  const subtitle = el('p', {
    text: 'Log in to access your ultra-fast 10M+ row workbooks',
    attrs: { style: 'font-size: 13px; color: #94a3b8; margin: 0 0 28px 0;' },
  });

  // Form Controls
  const form = el('form', {
    attrs: {
      novalidate: 'true',
      style: 'display: flex; flex-direction: column; gap: 16px; text-align: left;',
    },
  });

  const emailLabel = el('label', {
    text: 'Email address',
    attrs: {
      for: 'rz-email-input',
      style: 'font-size: 12px; font-weight: 600; color: #cbd5e1; display: block; margin-bottom: 6px;',
    },
  });

  const emailInput = el('input', {
    attrs: {
      id: 'rz-email-input',
      type: 'text',
      'aria-label': 'Email address',
      placeholder: 'name@numixsheet.com',
      autocomplete: 'username',
      style: `
        width: 100%; padding: 12px 14px; background: rgba(15, 23, 42, 0.6);
        border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 10px;
        color: #ffffff; font-size: 14px; outline: none; transition: border-color 0.2s;
        box-sizing: border-box;
      `,
    },
  }) as HTMLInputElement;

  emailInput.addEventListener('focus', () => (emailInput.style.borderColor = '#0d9488'));
  emailInput.addEventListener('blur', () => (emailInput.style.borderColor = 'rgba(255, 255, 255, 0.15)'));

  const passwordLabel = el('label', {
    text: 'Password',
    attrs: {
      for: 'rz-password-input',
      style: 'font-size: 12px; font-weight: 600; color: #cbd5e1; display: block; margin-bottom: 6px;',
    },
  });

  const passwordInput = el('input', {
    attrs: {
      id: 'rz-password-input',
      type: 'password',
      'aria-label': 'Password',
      placeholder: '••••••••',
      autocomplete: 'current-password',
      style: `
        width: 100%; padding: 12px 14px; background: rgba(15, 23, 42, 0.6);
        border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 10px;
        color: #ffffff; font-size: 14px; outline: none; transition: border-color 0.2s;
        box-sizing: border-box;
      `,
    },
  }) as HTMLInputElement;

  passwordInput.addEventListener('focus', () => (passwordInput.style.borderColor = '#0d9488'));
  passwordInput.addEventListener('blur', () => (passwordInput.style.borderColor = 'rgba(255, 255, 255, 0.15)'));

  const errorBox = el('div', {
    attrs: {
      style: 'font-size: 12px; color: #f87171; background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); padding: 8px 12px; border-radius: 8px; display: none;',
    },
  });

  const submitBtn = el('button', {
    text: 'Continue with Email',
    attrs: {
      type: 'submit',
      style: `
        width: 100%; padding: 12px; margin-top: 4px;
        background: linear-gradient(135deg, #0d9488 0%, #0284c7 100%);
        border: none; border-radius: 10px; color: #ffffff;
        font-size: 14px; font-weight: 600; cursor: pointer;
        box-shadow: 0 4px 14px rgba(13, 148, 136, 0.3); transition: transform 0.1s ease;
      `,
    },
  }) as HTMLButtonElement;

  const divider = el('div', {
    attrs: {
      style: 'display: flex; align-items: center; margin: 24px 0; color: #64748b; font-size: 12px;',
    },
  }, [
    el('div', { attrs: { style: 'flex: 1; height: 1px; background: rgba(255, 255, 255, 0.1);' } }),
    el('span', { text: 'OR', attrs: { style: 'padding: 0 12px;' } }),
    el('div', { attrs: { style: 'flex: 1; height: 1px; background: rgba(255, 255, 255, 0.1);' } }),
  ]);

  const googleBtn = el('button', {
    attrs: {
      id: 'rz-google-login-btn',
      type: 'button',
      style: `
        width: 100%; padding: 12px; background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 10px;
        color: #f8fafc; font-size: 14px; font-weight: 500; cursor: pointer;
        display: flex; align-items: center; justify-content: center; gap: 10px;
        transition: background 0.2s ease;
      `,
    },
  }, [
    el('span', { text: '🌐' }),
    el('span', { text: 'Continue with Google OAuth' }),
  ]) as HTMLButtonElement;

  const modeRow = el('div', {
    attrs: { style: 'display: flex; justify-content: space-between; align-items: center; font-size: 12px; margin-top: 4px;' },
  });

  const forgotLink = el('button', {
    text: 'Forgot password?',
    attrs: {
      type: 'button',
      style: 'background: none; border: none; color: #38bdf8; cursor: pointer; padding: 0; font-size: 12px; font-weight: 500;',
    },
  });

  const registerToggle = el('button', {
    text: 'Create account / Sign up',
    attrs: {
      type: 'button',
      style: 'background: none; border: none; color: #2dd4bf; cursor: pointer; padding: 0; font-size: 12px; font-weight: 600;',
    },
  });

  modeRow.append(forgotLink, registerToggle);

  // Token Input for Password Reset Mode
  const tokenLabel = el('label', {
    text: 'Reset Token',
    attrs: {
      for: 'rz-reset-token-input',
      style: 'font-size: 12px; font-weight: 600; color: #cbd5e1; display: none; margin-bottom: 6px;',
    },
  });

  const tokenInput = el('input', {
    attrs: {
      id: 'rz-reset-token-input',
      type: 'text',
      'aria-label': 'Password Reset Token',
      placeholder: 'Enter 64-character token',
      style: `
        width: 100%; padding: 12px 14px; background: rgba(15, 23, 42, 0.6);
        border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 10px;
        color: #ffffff; font-size: 14px; outline: none; transition: border-color 0.2s;
        box-sizing: border-box; display: none; margin-bottom: 12px;
      `,
    },
  }) as HTMLInputElement;

  let currentMode: 'login' | 'register' | 'reset' = 'login';

  const updateMode = (newMode: 'login' | 'register' | 'reset') => {
    currentMode = newMode;
    errorBox.style.display = 'none';
    if (currentMode === 'reset') {
      title.textContent = 'Reset Your Password';
      subtitle.textContent = 'Enter the reset token sent to your email and your new password';
      emailLabel.style.display = 'none';
      emailInput.style.display = 'none';
      tokenLabel.style.display = 'block';
      tokenInput.style.display = 'block';
      passwordLabel.textContent = 'New Secure Password';
      submitBtn.textContent = 'Update Password';
      registerToggle.textContent = 'Back to Sign In';
      forgotLink.style.display = 'none';
      divider.style.display = 'none';
      googleBtn.style.display = 'none';
    } else if (currentMode === 'register') {
      title.textContent = 'Create NumixSheet Account';
      subtitle.textContent = 'Sign up to start working with ultra-fast workbooks';
      emailLabel.style.display = 'block';
      emailInput.style.display = 'block';
      tokenLabel.style.display = 'none';
      tokenInput.style.display = 'none';
      passwordLabel.textContent = 'Password';
      submitBtn.textContent = 'Create Account';
      registerToggle.textContent = 'Already have an account? Sign in';
      forgotLink.style.display = 'none';
      divider.style.display = 'flex';
      googleBtn.style.display = 'flex';
    } else {
      title.textContent = 'Welcome to NumixSheet';
      subtitle.textContent = 'Log in to access your ultra-fast 10M+ row workbooks';
      emailLabel.style.display = 'block';
      emailInput.style.display = 'block';
      tokenLabel.style.display = 'none';
      tokenInput.style.display = 'none';
      passwordLabel.textContent = 'Password';
      submitBtn.textContent = 'Continue with Email';
      registerToggle.textContent = 'Create account / Sign up';
      forgotLink.style.display = 'inline-block';
      divider.style.display = 'flex';
      googleBtn.style.display = 'flex';
    }
  };

  const initialResetToken = typeof window !== 'undefined' && window.location
    ? new URLSearchParams(window.location.search).get('reset_token') || ''
    : '';

  const initialAuthError = typeof window !== 'undefined' && window.location
    ? new URLSearchParams(window.location.search).get('auth_error') ||
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('numix_oauth_error') || '' : '')
    : '';

  if (initialAuthError) {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('numix_oauth_error');
    }
    errorBox.textContent = initialAuthError;
    errorBox.style.display = 'block';
    if (typeof window !== 'undefined' && window.history && window.location.search.includes('auth_error')) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('auth_error');
      window.history.replaceState({}, '', cleanUrl.toString());
    }
  }

  if (initialResetToken) {
    tokenInput.value = initialResetToken;
    updateMode('reset');
  }

  registerToggle.addEventListener('click', () => {
    if (currentMode === 'login') {
      updateMode('register');
    } else {
      updateMode('login');
    }
  });

  forgotLink.addEventListener('click', async () => {
    errorBox.style.display = 'none';
    const email = emailInput.value;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errorBox.textContent = '⚠️ Please enter your email address above to reset password.';
      errorBox.style.display = 'block';
      return;
    }
    try {
      const res = await fetch(apiUrl('/api/auth/password/forgot'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = (await res.json()) as { message?: string; error?: string };
      errorBox.style.color = '#34d399';
      errorBox.textContent = 'ℹ️ ' + (data.message || 'If the account exists, reset instructions will be sent.');
      errorBox.style.display = 'block';
    } catch {
      errorBox.style.color = '#f87171';
      errorBox.textContent = '❌ Failed to reach password reset service.';
      errorBox.style.display = 'block';
    }
  });

  const handlePasswordReset = async (token: string, newPassword: string) => {
    errorBox.style.color = '#f87171';
    errorBox.style.display = 'none';

    if (!token) {
      errorBox.textContent = '⚠️ Please provide the password reset token.';
      errorBox.style.display = 'block';
      return;
    }

    if (newPassword.length < 8) {
      errorBox.textContent = '⚠️ Password must be at least 8 characters long.';
      errorBox.style.display = 'block';
      return;
    }
    if (newPassword.length > 128) {
      errorBox.textContent = '⚠️ Password cannot exceed 128 characters.';
      errorBox.style.display = 'block';
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(newPassword)) {
      errorBox.textContent = '⚠️ Password must contain uppercase, lowercase, and a number or symbol.';
      errorBox.style.display = 'block';
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = '🔒 Updating password...';

    try {
      const res = await fetch(apiUrl('/api/auth/password/reset'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = (await res.json()) as { ok?: boolean; message?: string; error?: string };

      if (!res.ok || !data.ok) {
        errorBox.textContent = data.error || '❌ Failed to reset password.';
        errorBox.style.display = 'block';
        submitBtn.disabled = false;
        submitBtn.textContent = 'Update Password';
        return;
      }

      updateMode('login');
      errorBox.style.color = '#34d399';
      errorBox.textContent = '✅ ' + (data.message || 'Password reset successfully. Please log in.');
      errorBox.style.display = 'block';
      submitBtn.disabled = false;
      submitBtn.textContent = 'Continue with Email';
    } catch {
      errorBox.textContent = '❌ Failed to reach password reset service.';
      errorBox.style.display = 'block';
      submitBtn.disabled = false;
      submitBtn.textContent = 'Update Password';
    }
  };

  const handleLoginOrRegister = async (emailRaw: string, passwordRaw: string) => {
    errorBox.style.color = '#f87171';
    errorBox.style.display = 'none';
    const email = emailRaw.trim();
    const password = passwordRaw;

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errorBox.textContent = '⚠️ Please enter a valid email address';
      errorBox.style.display = 'block';
      return;
    }

    if (!password) {
      errorBox.textContent = '⚠️ Password is required';
      errorBox.style.display = 'block';
      return;
    }

    if (currentMode === 'register') {
      if (password.length < 8) {
        errorBox.textContent = '⚠️ Password must be at least 8 characters long.';
        errorBox.style.display = 'block';
        return;
      }
      if (password.length > 128) {
        errorBox.textContent = '⚠️ Password cannot exceed 128 characters.';
        errorBox.style.display = 'block';
        return;
      }
      if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) {
        errorBox.textContent = '⚠️ Password must contain uppercase, lowercase, and a number or symbol.';
        errorBox.style.display = 'block';
        return;
      }
    }

    submitBtn.disabled = true;
    googleBtn.disabled = true;
    submitBtn.textContent = currentMode === 'register' ? '🔒 Creating account...' : '🔒 Signing you in...';

    const endpoint = currentMode === 'register' ? '/api/auth/email/register' : '/api/auth/email/login';

    try {
      const res = await fetch(apiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email, password }),
      });

      const data = (await res.json()) as { ok?: boolean; user?: UserProfile; token?: string; error?: string };

      if (!res.ok || !data.ok) {
        errorBox.textContent = data.error || (currentMode === 'register' ? '❌ Registration failed.' : '❌ Invalid email or password.');
        errorBox.style.display = 'block';
        submitBtn.disabled = false;
        googleBtn.disabled = false;
        submitBtn.textContent = currentMode === 'register' ? 'Create Account' : 'Continue with Email';
        return;
      }

      if (currentMode === 'register') {
        updateMode('login');
        submitBtn.disabled = false;
        googleBtn.disabled = false;
        handleLoginOrRegister(emailRaw, passwordRaw);
        return;
      }

      const userProfile: UserProfile = {
        name: data.user?.name || email.split('@')[0] || 'User',
        email: data.user?.email || email,
        avatar: data.user?.avatar || '⚡',
        isLoggedIn: true,
        ...(data.token ? { token: data.token } : {}),
      };
      setActiveUser(userProfile);
      options.onLoginSuccess(userProfile);
    } catch {
      errorBox.textContent = '❌ Authentication service is unavailable. Please try again later.';
      errorBox.style.display = 'block';
      submitBtn.disabled = false;
      googleBtn.disabled = false;
      submitBtn.textContent = currentMode === 'register' ? 'Create Account' : 'Continue with Email';
    }
  };

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (currentMode === 'reset') {
      handlePasswordReset(tokenInput.value, passwordInput.value);
    } else {
      handleLoginOrRegister(emailInput.value, passwordInput.value);
    }
  });

  const triggerGoogleOAuthFlow = async () => {
    submitBtn.disabled = true;
    googleBtn.disabled = true;
    googleBtn.textContent = '🔒 Redirecting to Google...';

    try {
      const googleClientId =
        (typeof import.meta !== 'undefined' && (import.meta as unknown as { env?: { VITE_GOOGLE_CLIENT_ID?: string } }).env?.VITE_GOOGLE_CLIENT_ID) ||
        (typeof window !== 'undefined' && (window as unknown as { __NUMIX_CONFIG__?: { googleClientId?: string } }).__NUMIX_CONFIG__?.googleClientId) ||
        '';

      if (!googleClientId) {
        errorBox.textContent = '⚠️ Google OAuth is not configured on this deployment. Please sign in with email.';
        errorBox.style.display = 'block';
        submitBtn.disabled = false;
        googleBtn.disabled = false;
        googleBtn.replaceChildren(
          el('span', { text: '🌐' }),
          el('span', { text: 'Continue with Google OAuth' }),
        );
        return;
      }

      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const scope = 'openid email profile';

      // RFC 6749 Section 10.12 CSRF State Protection
      const stateBytes = new Uint8Array(24);
      if (typeof window !== 'undefined' && window.crypto) {
        window.crypto.getRandomValues(stateBytes);
      }
      const state = Array.from(stateBytes, (b) => b.toString(16).padStart(2, '0')).join('');
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('numix_oauth_state', JSON.stringify({ state, timestamp: Date.now() }));
      }

      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(googleClientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=code&` +
        `scope=${encodeURIComponent(scope)}&` +
        `state=${encodeURIComponent(state)}&` +
        `access_type=offline&prompt=consent`;

      window.location.href = authUrl;
    } catch {
      errorBox.textContent = '❌ Google login is not available. Please use email login.';
      errorBox.style.display = 'block';
      submitBtn.disabled = false;
      googleBtn.disabled = false;
      googleBtn.replaceChildren(
        el('span', { text: '🌐' }),
        el('span', { text: 'Continue with Google OAuth' }),
      );
    }
  };

  googleBtn.addEventListener('click', () => {
    triggerGoogleOAuthFlow();
  });

  form.append(emailLabel, emailInput, tokenLabel, tokenInput, passwordLabel, passwordInput, modeRow, errorBox, submitBtn);
  card.append(logoBox, title, subtitle, form, divider, googleBtn);
  container.appendChild(card);

  return container;
}
